<template>
  <v-layout
    id="promo-code-form"
    justify-center
    wrap
  >
    <v-flex
      xs12
      md8
      lg8
    >
      <h2 v-if="!promoCode">
        {{ $t('modules.promoCodeSwap.promoCodeNew') }}
      </h2>
      <h2 v-else>
        {{ $t('modules.promoCodeSwap.promoCodeEdit') }}
      </h2>
      <v-card class="pt-7 pl-5 pr-5">
        <v-form>
          <v-layout wrap>
            <v-flex
              mt-4
              xs12
            >
              <v-text-field
                v-model.trim="form.name"
                :error-messages="NameErrors"
                :label="$t('modules.promoCodeSwap.name')+ '*'"
                required
                @input="$v.form.name.$touch()"
                @blur="$v.form.name.$touch()"
              />
            </v-flex>
            <v-flex
              xs12
              md4
            >
              <v-text-field
                v-model.trim.number="form.score"
                :error-messages="ScoreErrors"
                :label="$t('modules.promoCodeSwap.points')+'*'"
                @input="$v.form.score.$touch()"
                @blur="$v.form.score.$touch()"
              />
            </v-flex>
            <v-flex
              xs12
              md4
            >
              <v-text-field
                v-model.trim="form.discount"
                :error-messages="DiscountErrors"
                :label="$t('modules.promoCodeSwap.discount')+'*'"
                @input="$v.form.discount.$touch()"
                @blur="$v.form.discount.$touch()"
              />
            </v-flex>
            <v-flex
              xs12
              md4
            >
              <v-select
                v-model.trim="form.discount_type"
                :items="types"
                :item-text="'text'"
                :item-value="'id'"
                :error-messages="TypeErrors"
                :label="$t('modules.promoCodeSwap.type')+'*'"
                @change="$v.form.discount_type.$touch()"
                @blur="$v.form.discount_type.$touch()"
              />
            </v-flex>
            <v-flex
              xs12
            >
              <v-select
                v-model.trim="form.products"
                :items="moduleProducts"
                :item-text="'name'"
                :item-value="'id'"
                :label="$t('modules.promoCodeSwap.products')"
                multiple
              >
                <template v-slot:selection="{ item, index }">
                  <v-chip
                    v-if="index < 3"
                    class="info"
                    small
                  >
                    <span>{{ item.name }}</span>
                  </v-chip>
                  <span
                    v-if="index === 3"
                  >
                    <small>+ {{ $t('modules.productMore') }}</small>
                  </span>
                </template>
                <template
                  v-slot:append-outer
                >
                  <v-tooltip top>
                    <template v-slot:activator="{ on }">
                      <v-icon v-on="on">
                        mdi-information-outline
                      </v-icon>
                    </template>
                    {{ $t('modules.promoCodeSwap.productsHint') }}
                  </v-tooltip>
                </template>
              </v-select>
            </v-flex> 
            <v-flex
              xs12
            >
              <b>{{ $t("modules.promoCodeSwap.description") }}</b>
            </v-flex>
            <v-flex>
              <vue-editor
                id="editor"
                v-model="form.description"
                :editor-toolbar="customToolbar"
              />
            </v-flex>
            <v-flex
              xs12
              class="px-0"
            >
              <v-divider />
            </v-flex> 
          </v-layout>
          <v-layout>
            <v-flex
              xs12
              md12
            >
              <v-btn
                fab
                outlined
                small
                text
                @click="$emit('closeEditMode', false)"
              >
                <v-icon>mdi-arrow-left</v-icon>
              </v-btn>
              <v-btn
                color="primary"
                style="float:right"
                :disabled="loading || $v.$invalid"
                :loading="loading"
                @click="save()"
              >
                {{ $t('globals.saveButton') }}
              </v-btn>
            </v-flex>
          </v-layout>
        </v-form>
      </v-card>
    </v-flex>
  </v-layout>
</template>

<script>
import PRODUCTS_QUERY from "@/graphql/queries/modules/Products.gql";
import PROMO_CODE_CONFIG_CREATE_MUTATION from "@/graphql/mutations/modules/game/PromocodeConfigCreate.gql";
import PROMO_CODE_CONFIG_UPDATE_MUTATION from "@/graphql/mutations/modules/game/PromocodeConfigUpdate.gql";
import { validationMixin } from "vuelidate";
import {
  required,
  minLength,
  maxLength,
  numeric,
  integer,
  decimal,
} from "vuelidate/lib/validators";
import { VueEditor } from "vue2-editor";

export default {
  name: "ModuleGamePromoCodeConfigEditForm",
  components: {
    VueEditor
  },
  mixins: [validationMixin],
  props: {
    promoCode: {
      type: Object,
      default: null
    },
    whitelabelUuid: {
      type: String,
      default: null
    }
  },
  data() {
    return {
      loading: false,
      types: [
        {id: "percents", text: this.$t('modules.promoCodeSwap.percents')},
        {id: "units", text: this.$t('modules.promoCodeSwap.units')},
      ],
      customToolbar: [
        [{ 'header': [1, 2, 3, 4, 5, 6, false] }],
        ['bold', 'italic', 'underline', 'strike'],
        ['blockquote'],
        [{ 'list': 'ordered'}, { 'list': 'bullet' }],
        [{ 'script': 'sub'}, { 'script': 'super' }],
        [{ 'indent': '-1'}, { 'indent': '+1' }],
        [{ 'direction': 'rtl' }],
        [{ 'color': [] }, { 'background': [] }],
        [{ 'align': []}, 'link'],

        ['clean']
      ],
      form: {}
    }
  },
  computed: {
    rules() {
      return {
        name: {
          required,
          minLength: minLength(3),
          maxLength: maxLength(100),
        },
        score: {
          required,
          numeric,
          integer,
        },
        discount: {
          required,
          decimal
        },
        discount_type: {
         required,
        }
      }
    },
    NameErrors() {
      const errors = [];
      if (!this.$v.form.name.$dirty) return errors;
      !this.$v.form.name.required && errors.push(this.$t("globals.reqField"));
      !this.$v.form.name.minLength &&
        errors.push(this.$t("whitelabel.minError", {char: this.$v.form.name.$params.minLength.min}));
      !this.$v.form.name.maxLength &&
        errors.push(this.$t("whitelabel.maxError", {char: this.$v.form.name.$params.maxLength.max}));
      return errors;
    },
    ScoreErrors() {
      const errors = [];
      if (!this.$v.form.score.$dirty) return errors;
      !this.$v.form.score.required && errors.push(this.$t("globals.reqField"));
      !this.$v.form.score.numeric && errors.push(this.$t("globals.numberField"));
      !this.$v.form.score.integer && errors.push(this.$t("globals.numberField"));
      return errors;
    },
    DiscountErrors() {
      const errors = [];
      if (!this.$v.form.discount.$dirty) return errors;
      !this.$v.form.discount.required && errors.push(this.$t("globals.reqField"));
      !this.$v.form.discount.decimal && errors.push(this.$t("globals.numberField"));
      return errors;
    },
    TypeErrors() {
      const errors = [];
      if (!this.$v.form.discount_type.$dirty) return errors;
      !this.$v.form.discount_type.required && errors.push(this.$t("globals.reqField"));
      return errors;
    },
  },
  mounted() {
    const form = JSON.parse(JSON.stringify(this.promoCode));
    if (form) {      
      this.form = form;
      if(form.products.length !== 0) {
        this.form.products = form.products.map(item => item.id);
      }
    } else {
      this.form = {
        name: "",
        description: "",
        score: null,
        discount: null,
        discount_type: "",
        products: []
      }
    }
  },
  validations() {
    return {
      form: this.rules
    };
  },
  methods: {
    save() {
      this.$v.$touch();
      if (this.$v.$invalid) {
        return
      }
      this.loading = true;

      if(this.promoCode) this.form.config_uuid = this.promoCode.uuid;

      this.form.whitelabel_uuid = this.whitelabelUuid;
      this.form.discount = +this.form.discount;

      this.$apollo
        .mutate({
          mutation: !this.promoCode ? PROMO_CODE_CONFIG_CREATE_MUTATION : PROMO_CODE_CONFIG_UPDATE_MUTATION,
          variables: this.form
        })
        .then(data => {
          this.loading = false;
          this.$emit('closeEditMode', true)
        })
        .catch(error => {
          this.loading = false;
          this.errorHandler(error);
        });
    },
  },
  apollo: {
    moduleProducts: {
      fetchPolicy: "network-only",
      query() {
        return PRODUCTS_QUERY;
      },
      variables() {
        return {
            whitelabel_uuid: this.whitelabelUuid
          }
      },
      skip() {
        return !this.whitelabelUuid;
      },
    },
  },
}
</script>

<style lang="scss">
#promo-code-form {
  .image {
    width: 300px;
    margin: 0 auto;
    border: 1px solid #d2d2d2;
    border-radius: 5px;
    text-align: center;
    cursor: pointer;
    min-height: 150px;
    max-height: 150px;
    .image-preview {
      width: 300;
      overflow: hidden;
      border-radius: 5px;
    }
    i {
      margin: 5px 0 0px;
      font-size: 26px;
    }
    p {
      margin: 7px 0 0!important;
      &:first-of-type {
        font-weight: 700;
      }
      line-height: 0.85rem;
    }
    small {
      font-size: 11px;
    }
  }
}
</style>
